import { AxiosError } from 'axios'

/**
 * API error object structure (nested inside response.data.error)
 */
export interface ApiError {
  status: number
  message: string
  code: string
  details?: Record<string, unknown>
}

/**
 * Full API response structure for errors
 */
export interface ApiErrorResponse {
  data: null
  meta: Record<string, unknown>
  error: ApiError
}

/**
 * Validation error structure
 */
export interface ValidationError {
  field: string
  message: string
  code?: string
}

/**
 * Type guard to check if error is an Axios error with API error response
 */
export function isApiError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return (
    error instanceof AxiosError &&
    error.response?.data !== undefined &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'error' in error.response.data &&
    typeof (error.response.data as ApiErrorResponse).error === 'object' &&
    (error.response.data as ApiErrorResponse).error !== null &&
    'message' in (error.response.data as ApiErrorResponse).error
  )
}

/**
 * Type guard to check if error is a validation error (has details with field errors)
 */
export function isValidationError(error: unknown): error is AxiosError<ApiErrorResponse> {
  if (!isApiError(error)) return false
  const apiError = error.response?.data?.error
  return apiError?.code === 'validation_error' && apiError?.details !== undefined
}

/**
 * Type guard to check if error is a network error (no response from server)
 */
export function isNetworkError(error: unknown): error is AxiosError {
  return error instanceof AxiosError && !error.response
}

/**
 * Type guard to check if error is an authentication error (401)
 */
export function isAuthError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return isApiError(error) && error.response?.status === 401
}

/**
 * Type guard to check if error is a forbidden error (403)
 */
export function isForbiddenError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return isApiError(error) && error.response?.status === 403
}

/**
 * Type guard to check if error is a not found error (404)
 */
export function isNotFoundError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return isApiError(error) && error.response?.status === 404
}

/**
 * Type guard to check if error is a conflict error (409)
 */
export function isConflictError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return isApiError(error) && error.response?.status === 409
}

/**
 * Type guard to check if error is a server error (5xx)
 */
export function isInternalServerError(error: unknown): error is AxiosError<ApiErrorResponse> {
  return (
    isApiError(error) &&
    error.response?.status !== undefined &&
    error.response.status >= 500 &&
    error.response.status < 600
  )
}

/**
 * Extract error message from any error type
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.response?.data?.error?.message || 'Nastala chyba při komunikaci se serverem'
  }

  if (isNetworkError(error)) {
    return 'Nepodařilo se připojit k serveru. Zkontrolujte připojení k internetu.'
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return 'Nastala neznámá chyba'
}

/**
 * Extract error code from error
 */
export function getErrorCode(error: unknown): string | undefined {
  if (isApiError(error)) {
    return error.response?.data?.error?.code
  }
  return undefined
}

/**
 * Extract error details from error (e.g., validation field errors)
 */
export function getErrorDetails(error: unknown): Record<string, unknown> | undefined {
  if (isApiError(error)) {
    return error.response?.data?.error?.details
  }
  return undefined
}

/**
 * Extract validation errors from error details
 */
export function getValidationErrors(error: unknown): ValidationError[] {
  if (!isValidationError(error)) return []

  const details = error.response?.data?.error?.details
  if (!details || typeof details !== 'object') return []

  return Object.entries(details).map(([field, message]) => ({
    field,
    message: String(message)
  }))
}

/**
 * Extract status code from error
 */
export function getErrorStatusCode(error: unknown): number | null {
  if (error instanceof AxiosError && error.response?.status) {
    return error.response.status
  }

  return null
}

// Legacy aliases for backward compatibility
export const isServerError = isApiError
export type ServerError = ApiError
